import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { BASE_URL } from '../api/api';
import moment from 'moment';
import { ChevronLeft, ChevronRight, Calendar, AlertTriangle, Ban, Lock, Clock, User, Car, DollarSign, MapPin, Gauge, Tag, CreditCard, Shield, FileText, Camera, X } from 'lucide-react';
import './reservationform.css';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useData } from '../context/DataContext';

const EditReservationForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { clients, loadingClients, fetchClients, voitures, loadingVoitures, fetchVoitures } = useData();
    const isRtl = i18n.language === 'ar';

    const [form, setForm] = useState({
        client_name: '',
        client_id: null,
        voiture_id: null,
        lieu_depart: 'local',
        lieu_retour: 'local',
        km_depart: '',
        km_retour: '',
        date_depart: null,
        date_retour_prevue: null,
        date_retour_effective: '',
        prix_base: 0,
        remise: 0,
        remise_description: '',
        total: 0,
        total_paye: 0,
        mode_paiement: 'versement',
        reste_a_payer: 0,
        versement: 0,
        montant_daman: 0,
        daman_rendu: true,
        status: 'en_attente',
        agent_reservation: null,
        heure_depart: '08:00',
        heure_retour_prevue: '18:00'
    });
    const [clientStats, setClientStats] = useState(null);
    const [reservedPeriods, setReservedPeriods] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [success, setSuccess] = useState('');
    const [selectingStart, setSelectingStart] = useState(true);
    const [showCalendar, setShowCalendar] = useState(false);
    const [showTimePicker, setShowTimePicker] = useState(false);
    const [calendarType, setCalendarType] = useState('depart');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [hoveredDate, setHoveredDate] = useState(null);
    const [existingPhotos, setExistingPhotos] = useState([]);

    const isOngoing = form.status === 'en_cours';
    const previousTotalRef = useRef(0);


    // Load reservation data
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Ensure common data is loaded
                fetchClients();
                fetchVoitures();

                const [reservationRes, photosRes] = await Promise.all([
                    api.get(`/reservations/details/${id}`),
                    api.get(`/reservations/${id}/photos`)
                ]);

                const reservation = reservationRes.data;
                const departDate = reservation.date_depart ? new Date(reservation.date_depart) : null;
                const retourDate = reservation.date_retour_prevue ? new Date(reservation.date_retour_prevue) : null;

                const initialForm = {
                    ...reservation,
                    date_depart: departDate,
                    date_retour_prevue: retourDate,
                    heure_depart: departDate ? moment(departDate).format('HH:mm') : '08:00',
                    heure_retour_prevue: retourDate ? moment(retourDate).format('HH:mm') : '18:00'
                };

                setForm(initialForm);
                // Set initial previous total
                previousTotalRef.current = parseFloat(initialForm.total) || 0;

                setExistingPhotos(photosRes.data);

                if (reservation.voiture_id) {
                    api.get(`/reservations/periods/${reservation.voiture_id}`)
                        .then(res => setReservedPeriods(res.data))
                        .catch(err => {
                            console.error('Error fetching reserved periods:', err);
                            setReservedPeriods([]);
                        });
                }
            } catch (err) {
                console.error("Error fetching data:", err);
                setErrors([t('error_details')]);
            }
        };
        fetchData();
    }, [id]);

    useEffect(() => {
        if (form.client_id) {
            api.get(`/reservations/client/${form.client_id}/stats`)
                .then(res => setClientStats(res.data))
                .catch(() => setClientStats(null));
        }
    }, [form.client_id]);

    // In your useEffect that handles pricing
    useEffect(() => {
        if (!form.voiture_id) return;

        const selectedCar = voitures.find(v => v.id === form.voiture_id);
        if (!selectedCar) return;

        if (form.date_depart && form.date_retour_prevue) {
            const startDateTime = moment(`${moment(form.date_depart).format('YYYY-MM-DD')} ${form.heure_depart}`);
            const endDateTime = moment(`${moment(form.date_retour_prevue).format('YYYY-MM-DD')} ${form.heure_retour_prevue}`);

            const hours = Math.ceil(endDateTime.diff(startDateTime, 'hours', true));
            const days = Math.max(1, Math.ceil(hours / 24));

            const dailyRate = selectedCar.prix_location_jour;
            const totalBeforeDiscount = dailyRate * days;

            const safeRemise = Math.min(form.remise, totalBeforeDiscount);
            const newTotal = totalBeforeDiscount - safeRemise;

            // Check if total increased and payment mode is "paye"
            const oldTotal = previousTotalRef.current;
            let newModePaiement = form.mode_paiement;
            let newVersement = form.versement;
            let newTotalPaye = form.total_paye;

            if (newTotal > oldTotal && form.mode_paiement === 'paye') {
                // Auto-switch to versement and set versement to old total
                newModePaiement = 'versement';
                newVersement = oldTotal;
                newTotalPaye = oldTotal;

                Swal.fire({
                    icon: 'info',
                    title: t('payment_mode_modified'),
                    text: `${t('total_increased_from')} ${oldTotal} ${t('currency')} ${t('to')} ${newTotal} ${t('currency')}. ${t('payment_mode_auto_changed')}`,
                    confirmButtonColor: '#3b82f6'
                });
            } else if (newTotal > oldTotal && form.mode_paiement === 'versement') {
                // If already in versement mode and total increases, keep current versement but adjust reste_a_payer
                newVersement = Math.min(form.versement, newTotal);
                newTotalPaye = newVersement;
            }

            setForm(prev => ({
                ...prev,
                prix_base: dailyRate,
                remise: safeRemise,
                total: newTotal,
                mode_paiement: newModePaiement,
                versement: newVersement,
                total_paye: newTotalPaye,
                reste_a_payer: newTotal - newVersement
            }));

            // Update previous total for next calculation
            previousTotalRef.current = newTotal;
        }
    }, [
        form.voiture_id,
        form.date_depart,
        form.date_retour_prevue,
        form.heure_depart,
        form.heure_retour_prevue,
        form.remise,
        form.mode_paiement,
        form.versement
    ]);

    // Si on sélectionne une date de départ, on peut automatiquement ouvrir le calendrier de retour
    useEffect(() => {
        if (form.date_depart && !form.date_retour_prevue && !selectingStart) {
            setCalendarType('retour');
            setShowCalendar(true);
        }
    }, [form.date_depart, form.date_retour_prevue, selectingStart]);

    const isDateInPeriod = (date, period) => {
        const checkDate = new Date(date);
        const startDate = new Date(period.start);
        const endDate = new Date(period.end);

        checkDate.setHours(0, 0, 0, 0);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);

        return checkDate >= startDate && checkDate <= endDate;
    };

    // Dans la fonction renderCalendar(), modifiez la partie du statut des dates :
    const getDateStatus = (date) => {
        // Pour les réservations en cours, ignorer les vérifications de conflit
        if (isOngoing) {
            return null;
        }

        for (const period of reservedPeriods) {
            if (isDateInPeriod(date, period)) {
                // Skip checking against the current reservation's own period
                if (period.reservation_id && period.reservation_id.toString() === id) {
                    return null;
                }
                return period.status;
            }
        }
        return null;
    };


    const isDateSelectable = (date) => {
        // Pour les réservations en cours, permettre la sélection de toutes les dates
        if (isOngoing) {
            return true;
        }

        // Pour les autres statuts, appliquer les restrictions normales
        const status = getDateStatus(date);
        if (status === 'en_cours') {
            return false;
        }
        return true;
    };

    const handleDateClick = async (date) => {
        // Pour les réservations en cours, ignorer toutes les vérifications de disponibilité
        if (!isOngoing) {
            const status = getDateStatus(date);

            if (status === 'en_cours') {
                Swal.fire({
                    icon: 'error',
                    title: t('date_unavailable'),
                    text: t('date_unavailable_in_progress'),
                    confirmButtonColor: '#3b82f6'
                });
                return;
            }

            if (status === 'en_attente') {
                const result = await Swal.fire({
                    title: t('warning'),
                    text: t('pending_date_warning'),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: t('continue'),
                    cancelButtonText: t('cancel'),
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33'
                });

                if (!result.isConfirmed) return;
            }
        }

        if (calendarType === 'depart') {
            setForm(prev => ({
                ...prev,
                date_depart: date,
                date_retour_prevue: prev.date_retour_prevue
            }));
            setSelectingStart(false);
        } else {
            setForm(prev => ({
                ...prev,
                date_retour_prevue: date
            }));
            setSelectingStart(true);

            // Trigger recalculation when return date changes
            recalculatePricing();
        }
        setShowCalendar(false);
        setShowTimePicker(true);
    };
    const recalculatePricing = () => {
        if (!form.voiture_id || !form.date_depart || !form.date_retour_prevue) return;

        const selectedCar = voitures.find(v => v.id === form.voiture_id);
        if (!selectedCar) return;

        const startDateTime = moment(`${moment(form.date_depart).format('YYYY-MM-DD')} ${form.heure_depart}`);
        const endDateTime = moment(`${moment(form.date_retour_prevue).format('YYYY-MM-DD')} ${form.heure_retour_prevue}`);

        const hours = Math.ceil(endDateTime.diff(startDateTime, 'hours', true));
        const days = Math.max(1, Math.ceil(hours / 24));

        const dailyRate = selectedCar.prix_location_jour;
        const totalBeforeDiscount = dailyRate * days;

        const safeRemise = Math.min(form.remise, totalBeforeDiscount);
        const newTotal = totalBeforeDiscount - safeRemise;

        // Check if total increased and payment mode is "paye"
        const oldTotal = previousTotalRef.current;
        let newModePaiement = form.mode_paiement;
        let newVersement = form.versement;
        let newTotalPaye = form.total_paye;

        if (newTotal > oldTotal && form.mode_paiement === 'paye') {
            // Auto-switch to versement and set versement to old total
            newModePaiement = 'versement';
            newVersement = oldTotal;
            newTotalPaye = oldTotal;

            Swal.fire({
                icon: 'info',
                title: t('payment_mode_modified'),
                text: `${t('total_increased_from')} ${oldTotal} ${t('currency')} ${t('to')} ${newTotal} ${t('currency')}. ${t('payment_mode_auto_changed')}`,
                confirmButtonColor: '#3b82f6'
            });
        }

        setForm(prev => ({
            ...prev,
            prix_base: dailyRate,
            remise: safeRemise,
            total: newTotal,
            mode_paiement: newModePaiement,
            versement: newVersement,
            total_paye: newTotalPaye,
            reste_a_payer: newTotal - newVersement
        }));

        // Update previous total for next calculation
        previousTotalRef.current = newTotal;
    };

    const handleTimeChange = (e, type) => {
        const { value } = e.target;
        setForm(prev => ({
            ...prev,
            [type === 'depart' ? 'heure_depart' : 'heure_retour_prevue']: value
        }));

        // Recalculate pricing when time changes for ongoing reservations too
        if (form.date_depart && form.date_retour_prevue) {
            setTimeout(() => recalculatePricing(), 100);
        }
    };

    const renderTimePicker = () => {
        return (
            <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 absolute z-10 mt-1 w-full max-w-xs">
                <h3 className="text-md font-semibold mb-3">
                    {t('select_time')} {calendarType === 'depart' ? t('departure') : t('return')}
                </h3>
                <div className="flex items-center gap-2">
                    <Clock className="text-gray-600" />
                    <input
                        type="time"
                        value={calendarType === 'depart' ? form.heure_depart : form.heure_retour_prevue}
                        onChange={(e) => handleTimeChange(e, calendarType)}
                        className="p-2 border rounded"
                    />
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        onClick={() => setShowTimePicker(false)}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        {t('confirm')}
                    </button>
                </div>
            </div>
        );
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
        }

        return days;
    };

    const navigateMonth = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + direction);
        setCurrentDate(newDate);
    };

    const renderCalendar = () => {
        const days = getDaysInMonth(currentDate);
        const monthNames = [
            t('january'), t('february'), t('march'), t('april'), t('may'), t('june'),
            t('july'), t('august'), t('september'), t('october'), t('november'), t('december')
        ];
        const dayNames = [
            t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')
        ];

        return (
            <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 absolute z-10 mt-1 w-full max-w-md">
                <div className="flex items-center justify-between mb-4">
                    <button
                        type="button"
                        onClick={() => navigateMonth(-1)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>

                    <h2 className="text-lg font-semibold text-gray-800">
                        {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h2>

                    <button
                        type="button"
                        onClick={() => navigateMonth(1)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                <div className="flex flex-wrap gap-3 mb-4 text-xs">
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-red-500 rounded"></div>
                        <span className="text-gray-600">{t('in_progress_no_select')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-yellow-400 rounded"></div>
                        <span className="text-gray-600">{t('pending_with_alert')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
                        <span className="text-gray-600">{t('available')}</span>
                    </div>
                </div>

                <div className="grid grid-cols-7 gap-1">
                    {dayNames.map((day) => (
                        <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                            {day}
                        </div>
                    ))}

                    {days.map((date, index) => {
                        if (!date) {
                            return <div key={index} className="aspect-square"></div>;
                        }

                        const status = getDateStatus(date);
                        const isSelectable = isDateSelectable(date);
                        const isToday = date.toDateString() === new Date().toDateString();
                        const isPast = date < new Date().setHours(0, 0, 0, 0);
                        const isSelectedStart = form.date_depart && date.toDateString() === form.date_depart.toDateString();
                        const isSelectedEnd = form.date_retour_prevue && date.toDateString() === form.date_retour_prevue.toDateString();

                        let cellClasses = 'aspect-square flex items-center justify-center text-sm rounded-lg transition-all duration-200 relative ';

                        if (isPast && !isSelectable) {
                            cellClasses += 'text-gray-400 cursor-not-allowed bg-gray-50 ';
                        } else if (status === 'en_cours') {
                            cellClasses += 'bg-red-500 text-white cursor-not-allowed ';
                        } else if (status === 'en_attente') {
                            cellClasses += 'bg-yellow-400 text-black cursor-pointer hover:bg-yellow-500 ';
                        } else if (isSelectable) {
                            cellClasses += 'hover:bg-blue-50 cursor-pointer text-gray-700 ';
                        } else {
                            cellClasses += 'cursor-not-allowed text-gray-400 ';
                        }

                        if (isToday) {
                            cellClasses += 'ring-2 ring-blue-400 ';
                        }

                        if (isSelectedStart || isSelectedEnd) {
                            cellClasses += 'bg-blue-500 text-white ring-2 ring-blue-600 ';
                        }

                        return (
                            <div
                                key={index}
                                className={cellClasses}
                                onClick={() => {
                                    if (isSelectable && !isPast) {
                                        handleDateClick(date);
                                    }
                                }}
                                onMouseEnter={() => setHoveredDate(date)}
                                onMouseLeave={() => setHoveredDate(null)}
                            >
                                <span className="relative z-10">{date.getDate()}</span>

                                {status === 'en_cours' && (
                                    <Ban className="absolute top-0 right-0 w-3 h-3 text-white opacity-75" />
                                )}
                                {status === 'en_attente' && (
                                    <AlertTriangle className="absolute top-0 right-0 w-3 h-3 text-orange-600" />
                                )}
                            </div>
                        );
                    })}
                </div>

                // Et dans le rendu des cellules du calendrier, modifiez le tooltip :
                {hoveredDate && (
                    <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
                        {isOngoing ? (
                            <div className="flex items-center gap-2 text-blue-600">
                                <Calendar className="w-4 h-4" />
                                <span>{t('date_unavailable_in_progress')}</span>
                            </div>
                        ) : (() => {
                            const status = getDateStatus(hoveredDate);
                            if (status === 'en_cours') {
                                return (
                                    <div className="flex items-center gap-2 text-red-600">
                                        <Ban className="w-4 h-4" />
                                        <span>{t('date_unavailable_in_progress')}</span>
                                    </div>
                                );
                            }
                            if (status === 'en_attente') {
                                return (
                                    <div className="flex items-center gap-2 text-yellow-600">
                                        <AlertTriangle className="w-4 h-4" />
                                        <span>{t('pending_click_continue')}</span>
                                    </div>
                                );
                            }
                            return (
                                <div className="flex items-center gap-2 text-green-600">
                                    <Calendar className="w-4 h-4" />
                                    <span>{hoveredDate.toLocaleDateString(i18n.language === 'ar' ? 'ar-DZ' : 'fr-FR')} - {t('available')}</span>
                                </div>
                            );
                        })()}
                    </div>
                )}
            </div>
        );
    };

    const formatDateTimeDisplay = (date, time) => {
        if (!date) return t('not_defined');
        return `${moment(date).format('DD/MM/YYYY')} ${t('at')} ${time}`;
    };

    const validateForm = () => {
        const newErrors = [];
        const departDateTime = moment(`${moment(form.date_depart).format('YYYY-MM-DD')} ${form.heure_depart}`);
        const retourPrevueDateTime = moment(`${moment(form.date_retour_prevue).format('YYYY-MM-DD')} ${form.heure_retour_prevue}`);

        if (!form.client_id && !form.client_name?.trim()) {
            newErrors.push(t('client_required'));
        }

        if (!form.voiture_id) {
            newErrors.push(t('vehicle_required'));
        }

        if (!form.km_depart || form.km_depart < 0) {
            newErrors.push(t('departure_km_invalid'));
        }

        if (!form.date_depart) {
            newErrors.push(t('departure_date_required'));
        }

        if (!form.date_retour_prevue) {
            newErrors.push(t('return_date_required'));
        }

        if (!isOngoing && form.date_depart && form.date_retour_prevue && departDateTime.isAfter(retourPrevueDateTime)) {
            newErrors.push(t('return_after_departure_error'));
        }

        if (form.remise < 0 || form.remise > form.prix_base) {
            newErrors.push(`${t('discount_range_error')} ${form.prix_base}`);
        }

        // Payment validation based on mode
        if (form.mode_paiement === 'versement') {
            if (form.versement <= 0) {
                newErrors.push(t('versement_min_error'));
            }
            if (form.versement > form.total) {
                newErrors.push(t('versement_max_error'));
            }
        } else if (form.mode_paiement === 'paye') {
            if (form.total_paye !== form.total) {
                newErrors.push(t('paid_mode_error'));
            }
        }
        // For 'non_paye', no validation needed - it's always 0

        setErrors(newErrors);
        return newErrors.length === 0;
    };
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleVoitureSelect = async (e) => {
        const voitureId = parseInt(e.target.value);
        const selectedCar = voitures.find(v => v.id === voitureId);

        if (selectedCar) {
            setForm(prev => ({
                ...prev,
                voiture_id: voitureId,
                km_depart: selectedCar.kilometrage,
                prix_base: selectedCar.prix_location_jour, // Set the daily rate immediately
                // Don't reset total to 0 - let the useEffect recalculate it
            }));

            // Recalculate pricing if dates are already set
            if (form.date_depart && form.date_retour_prevue) {
                setTimeout(() => recalculatePricing(), 100);
            }
        }
    };
    const handlePaymentModeChange = (e) => {
        const mode = e.target.value;
        const newTotalPaye = mode === 'paye' ? form.total : Math.min(form.versement, form.total);
        const newVersement = mode === 'paye' ? 0 : form.versement;

        setForm(prev => ({
            ...prev,
            mode_paiement: mode,
            total_paye: newTotalPaye,
            versement: newVersement,
            reste_a_payer: form.total - newVersement
        }));

        // Update previous total when manually changing payment mode
        previousTotalRef.current = form.total;
    };
    const handleVersementChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        const newTotalPaye = form.mode_paiement === 'paye' ? form.total : value;

        setForm(prev => ({
            ...prev,
            versement: value,
            reste_a_payer: prev.total - value,
            total_paye: newTotalPaye
        }));
    };

    const handleDeletePhoto = async (photoId) => {
        try {
            await api.delete(`/reservations/photos/${photoId}`);
            setExistingPhotos(prev => prev.filter(photo => photo.id !== photoId));
            setSuccess(t('success_delete_photo'));
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setErrors([t('error_delete_photo')]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors([]);
        setSuccess('');

        if (!validateForm()) {
            setSubmitting(false);
            return;
        }

        try {
            // Combine dates with times
            const date_depart_with_time = moment(`${moment(form.date_depart).format('YYYY-MM-DD')} ${form.heure_depart}`).toISOString();
            const date_retour_prevue_with_time = moment(`${moment(form.date_retour_prevue).format('YYYY-MM-DD')} ${form.heure_retour_prevue}`).toISOString();

            const formData = {
                ...form,
                agent_reservation: 1,
                client_id: form.client_id || null,
                client_name: form.client_id ? null : form.client_name?.trim(),
                voiture_id: parseInt(form.voiture_id),
                km_depart: parseInt(form.km_depart),
                km_retour: form.km_retour ? parseInt(form.km_retour) : null,
                prix_base: parseFloat(form.prix_base),
                remise: parseFloat(form.remise || 0),
                total: parseFloat(form.total),
                total_paye: parseFloat(form.total_paye || 0),
                versement: parseFloat(form.versement || 0),
                montant_daman: parseFloat(form.montant_daman || 0),
                daman_rendu: !!form.daman_rendu,
                status: form.status,
                date_depart: date_depart_with_time,
                date_retour_prevue: date_retour_prevue_with_time
            };

            const endpoint = form.status === 'en_cours'
                ? `/reservations/ongoing/${id}`
                : `/reservations/${id}`;

            // Ensure data consistency with SQL constraints
            if (formData.mode_paiement === 'paye') {
                formData.total_paye = formData.total;
            } else if (formData.mode_paiement === 'non_paye') {
                formData.total_paye = 0;
            }

            const response = await api.put(endpoint, formData);

            if (photos.length > 0) {
                const formDataPhotos = new FormData();
                photos.forEach(photo => {
                    formDataPhotos.append('photos', photo);
                });
                await api.post(
                    `/reservations/${id}/photos`,
                    formDataPhotos,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
            }

            setSuccess(`✅ ${t('success_update_reservation')}`);
            setTimeout(() => navigate(-1), 1000);
        } catch (err) {
            const errorMsg = err.response?.data?.message ||
                err.response?.data?.error ||
                t('error_update_reservation');
            setErrors([errorMsg]);
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <div className="max-w-5xl mx-auto p-6 bg-white rounded-xl shadow-lg" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="mb-8 flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">
                        {isOngoing ? t('edit_reservation_ongoing') : t('edit_reservation')}
                    </h1>
                    <p className="text-gray-600">{t('edit_reservation_desc')}</p>
                </div>
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
                >
                    <X className="w-5 h-5" /> {t('close')}
                </button>
            </div>

            {errors.length > 0 && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <AlertTriangle className="h-5 w-5 text-red-500" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">{t('errors_to_fix')}</h3>
                            <div className="mt-2 text-sm text-red-700">
                                <ul className="list-disc pl-5 space-y-1">
                                    {errors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {success && (
                <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 mb-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-green-800">{success}</p>
                        </div>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Client & Vehicle Section */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <User className={`${isRtl ? 'ml-2' : 'mr-2'} h-5 w-5 text-blue-600`} />
                        {t('client_vehicle_info')}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className={isOngoing ? 'opacity-70' : ''}>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <User className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('clients')} {isOngoing && <Lock className={`${isRtl ? 'mr-1' : 'ml-1'} w-4 h-4 text-gray-500`} />}
                            </label>
                            <select
                                name="client_id"
                                value={form.client_id || ''}
                                onChange={handleChange}
                                className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 ${isOngoing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                disabled={isOngoing}
                            >
                                <option value="">{t('select_client_placeholder')}</option>
                                {clients.map(c => (
                                    <option key={c.id} value={c.id}>{c.nom} {c.prenom}</option>
                                ))}
                            </select>
                            {!isOngoing && (
                                <div className="mt-2 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <User className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="client_name"
                                        value={form.client_name}
                                        onChange={handleChange}
                                        placeholder={t('search_client_or_enter')}
                                        className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                        disabled={form.client_id}
                                    />
                                </div>
                            )}
                        </div>

                        <div className={isOngoing ? 'opacity-70' : ''}>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <Car className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('vehicles')} {isOngoing && <Lock className={`${isRtl ? 'mr-1' : 'ml-1'} w-4 h-4 text-gray-500`} />}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Car className="h-5 w-5 text-gray-400" />
                                </div>
                                <select
                                    name="voiture_id"
                                    value={form.voiture_id || ''}
                                    onChange={handleVoitureSelect}
                                    className={`pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 ${isOngoing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    required
                                    disabled={isOngoing}
                                >
                                    <option value="">{t('select_vehicle_placeholder')}</option>
                                    {voitures
                                        .filter(v => v.disponible === 1 || v.id === form.voiture_id)
                                        .map(v => (
                                            <option key={v.id} value={v.id}>
                                                {v.marque} {v.modele} - {v.matricule} ({v.prix_location_jour}DA/jour)
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>
                        </div>
                    </div>

                    {clientStats && (
                        <div className="mt-4 bg-white p-3 rounded-lg border border-gray-200">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">📊 {t('client_history')}</h3>
                            <div className="grid grid-cols-3 gap-2 text-sm">
                                <div className="bg-yellow-50 p-2 rounded text-center">
                                    <p className="font-medium text-yellow-800">⏳ {t('pending')}</p>
                                    <p className="text-lg font-bold">{clientStats.en_attente || 0}</p>
                                </div>
                                <div className="bg-red-50 p-2 rounded text-center">
                                    <p className="font-medium text-red-800">🚗 {t('rental_status')}</p>
                                    <p className="text-lg font-bold">{clientStats.en_cours || 0}</p>
                                </div>
                                <div className="bg-green-50 p-2 rounded text-center">
                                    <p className="font-medium text-green-800">✅ {t('completed')}</p>
                                    <p className="text-lg font-bold">{clientStats.done || 0}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Dates Section */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <Calendar className={`${isRtl ? 'ml-2' : 'mr-2'} h-5 w-5 text-blue-600`} />
                        {t('rental_period')}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <Calendar className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('departure_date_time')} {isOngoing && <Lock className={`${isRtl ? 'mr-1' : 'ml-1'} w-4 h-4 text-gray-500`} />}
                            </label>
                            <div
                                className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between ${isOngoing ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer hover:border-blue-500'
                                    }`}
                                onClick={() => {
                                    if (isOngoing) return;
                                    setCalendarType('depart');
                                    setShowCalendar(!showCalendar);
                                    setShowTimePicker(false);
                                }}
                            >
                                <span className={form.date_depart ? "text-gray-800" : "text-gray-500"}>
                                    {formatDateTimeDisplay(form.date_depart, form.heure_depart)}
                                </span>
                                <div className="flex">
                                    <Clock className="w-5 h-5 text-gray-500" />
                                </div>
                            </div>
                            {showCalendar && calendarType === 'depart' && !isOngoing && renderCalendar()}
                            {showTimePicker && calendarType === 'depart' && !isOngoing && renderTimePicker()}
                        </div>

                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <Calendar className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('return_date_time')}
                            </label>
                            <div
                                className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between cursor-pointer hover:border-blue-500`}
                                onClick={() => {
                                    if (!form.date_depart) {
                                        Swal.fire({
                                            icon: 'warning',
                                            text: t('select_departure_first'),
                                            confirmButtonColor: '#3b82f6'
                                        });
                                        return;
                                    }
                                    setCalendarType('retour');
                                    setShowCalendar(!showCalendar);
                                    setShowTimePicker(false);
                                }}
                            >
                                <span className={form.date_retour_prevue ? "text-gray-800" : "text-gray-500"}>
                                    {formatDateTimeDisplay(form.date_retour_prevue, form.heure_retour_prevue)}
                                </span>
                                <div className="flex">
                                    <Clock className="w-5 h-5 text-gray-500" />
                                </div>
                            </div>
                            {showCalendar && calendarType === 'retour' && renderCalendar()}
                            {showTimePicker && calendarType === 'retour' && renderTimePicker()}
                        </div>
                    </div>

                    {form.date_depart && form.date_retour_prevue && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                                <label className="block text-xs font-medium text-gray-500 mb-1">{t('base_price_day')}</label>
                                <div className="flex items-center">
                                    <DollarSign className="h-4 w-4 text-gray-500 mr-1" />
                                    <span className="text-lg font-semibold">
                                        {form.prix_base}{t('currency')} {/* This now shows daily rate */}
                                    </span>
                                </div>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                                <label className="block text-xs font-medium text-gray-500 mb-1">{t('number_of_days')}</label>
                                <span className="text-lg font-semibold">
                                    {Math.ceil(
                                        moment(`${moment(form.date_retour_prevue).format('YYYY-MM-DD')} ${form.heure_retour_prevue}`)
                                            .diff(
                                                moment(`${moment(form.date_depart).format('YYYY-MM-DD')} ${form.heure_depart}`),
                                                'hours',
                                                true
                                            ) / 24
                                    )}
                                </span>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                                <label className="block text-xs font-medium text-gray-500 mb-1">{t('total_price_before_discount')}</label>
                                <div className="flex items-center">
                                    <DollarSign className="h-4 w-4 text-gray-500 mr-1" />
                                    <span className="text-lg font-semibold">
                                        {/* Calculate total base price for display */}
                                        {form.prix_base * Math.ceil(
                                            moment(`${moment(form.date_retour_prevue).format('YYYY-MM-DD')} ${form.heure_retour_prevue}`)
                                                .diff(
                                                    moment(`${moment(form.date_depart).format('YYYY-MM-DD')} ${form.heure_depart}`),
                                                    'hours',
                                                    true
                                                ) / 24
                                        )}{t('currency')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Location Details Section */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <MapPin className={`${isRtl ? 'ml-2' : 'mr-2'} h-5 w-5 text-blue-600`} />
                        {t('dates_locations')}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <MapPin className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('departure_location')}
                            </label>
                            <div className="relative">
                                <div
                                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MapPin className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="lieu_depart"
                                    value={form.lieu_depart}
                                    onChange={handleChange}
                                    className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <MapPin className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('return_location')}
                            </label>
                            <div className="relative">
                                <div
                                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MapPin className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="lieu_retour"
                                    value={form.lieu_retour}
                                    onChange={handleChange}
                                    className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>

                        <div className={isOngoing ? 'opacity-70' : ''}>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <Gauge className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('km_departure')} {isOngoing && <Lock className={`${isRtl ? 'mr-1' : 'ml-1'} w-4 h-4 text-gray-500`} />}
                            </label>
                            <div className="relative">
                                <div
                                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Gauge className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    name="km_depart"
                                    value={form.km_depart}
                                    onChange={handleChange}
                                    className={`pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 ${isOngoing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    min="0"
                                    required
                                    disabled={isOngoing}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <Gauge className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('return_mileage')}
                            </label>
                            <div className="relative">
                                <div
                                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Gauge className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    name="km_retour"
                                    value={form.km_retour || ''}
                                    onChange={handleChange}
                                    className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    min={form.km_depart || 0}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pricing Section */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <DollarSign className={`${isRtl ? 'ml-2' : 'mr-2'} h-5 w-5 text-blue-600`} />
                        {t('pricing_payment')}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <Tag className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('discount')}
                            </label>
                            <div className="relative">
                                <div
                                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <DollarSign className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    name="remise"
                                    value={form.remise}
                                    onChange={handleChange}
                                    className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    min="0"
                                    max={form.prix_base}
                                    step="0.01"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <DollarSign className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('total_after_discount')}
                            </label>
                            <div className="relative">
                                <div
                                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <DollarSign className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    name="total"
                                    value={form.total}
                                    readOnly
                                    className="pl-10 w-full py-2 border border-gray-300 rounded-lg bg-gray-100"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <CreditCard className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('payment_status')}
                            </label>
                            <div className="relative">
                                <div
                                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <CreditCard className="h-5 w-5 text-gray-400" />
                                </div>
                                <select
                                    name="mode_paiement"
                                    value={form.mode_paiement}
                                    onChange={handlePaymentModeChange}
                                    className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="versement">{t('partial')}</option>
                                    <option value="paye">{t('paid')}</option>
                                    <option value="non_paye">{t('unpaid')}</option> {/* Add this option */}
                                </select>
                            </div>
                        </div>

                        {form.mode_paiement === 'versement' && previousTotalRef.current > 0 && (
                            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mb-6 col-span-2">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <CreditCard className="h-5 w-5 text-blue-500" />
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-blue-800">
                                            {t('payment_mode_modified')}
                                        </p>
                                        <p className="text-sm text-blue-700 mt-1">
                                            {t('total_increased_from')} {previousTotalRef.current} {t('currency')} {t('to')} {form.total} {t('currency')}.
                                            {t('payment_mode_auto_changed')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {form.mode_paiement === 'versement' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                        <DollarSign className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                        {t('initial_versement')}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <DollarSign className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            name="versement"
                                            value={form.versement}
                                            onChange={handleVersementChange}
                                            className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                            min="0"
                                            max={form.total}
                                            step="0.01"
                                        />
                                    </div>
                                    {previousTotalRef.current > 0 && form.total > previousTotalRef.current && (
                                        <p className="text-xs text-blue-600 mt-1">
                                            {t('total')} {previousTotalRef.current} {t('currency')} | {t('new_total')} {form.total} {t('currency')}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                        <DollarSign className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                        {t('remaining_payment')}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <DollarSign className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            value={form.reste_a_payer}
                                            readOnly
                                            className="pl-10 w-full py-2 border border-gray-300 rounded-lg bg-gray-100"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {form.mode_paiement === 'paye' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                    <DollarSign className="mr-1 h-4 w-4 text-gray-500" />
                                    {t('amount_paid')}
                                </label>
                                <div className="relative">
                                    <div
                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <DollarSign className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="number"
                                        name="total_paye"
                                        value={form.total_paye}
                                        readOnly
                                        className="pl-10 w-full py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                                    />
                                </div>
                            </div>
                        )}
                        {form.mode_paiement === 'non_paye' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                    <DollarSign className="mr-1 h-4 w-4 text-gray-500" />
                                    {t('amount_paid')}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <DollarSign className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="number"
                                        value="0"
                                        readOnly
                                        className="pl-10 w-full py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Deposit & Notes Section */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <Shield className={`${isRtl ? 'ml-2' : 'mr-2'} h-5 w-5 text-blue-600`} />
                        {t('deposit_notes')}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <Shield className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('deposit')}
                            </label>
                            <div className="relative">
                                <div
                                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <DollarSign className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    name="montant_daman"
                                    value={form.montant_daman}
                                    onChange={handleChange}
                                    className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-start pt-6">
                            <input
                                type="checkbox"
                                name="daman_rendu"
                                checked={true}
                                onChange={handleChange}
                                id="daman_rendu"
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="daman_rendu" className="ml-2 block text-sm text-gray-700">
                                {t('deposit_returned')}
                            </label>
                        </div>
                    </div>

                    <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <FileText className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                            {t('description')}
                        </label>
                        <textarea
                            name="remise_description"
                            value={form.remise_description || ''}
                            onChange={handleChange}
                            className="w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                            placeholder={t('notes_placeholder')}
                            rows={3}
                        />
                    </div>

                    <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <Camera className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                            {t('photos_optional')}
                        </label>
                        <div
                            className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                            <div className="space-y-1 text-center">
                                <svg
                                    className="mx-auto h-12 w-12 text-gray-400"
                                    stroke="currentColor"
                                    fill="none"
                                    viewBox="0 0 48 48"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <div className="flex text-sm text-gray-600">
                                    <label
                                        htmlFor="file-upload"
                                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                                    >
                                        <span>{t('upload_files')}</span>
                                        <input
                                            id="file-upload"
                                            name="file-upload"
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={(e) => setPhotos([...e.target.files])}
                                            className="sr-only"
                                        />
                                    </label>
                                    <p className="pl-1">{t('drag_drop')}</p>
                                </div>
                                <p className="text-xs text-gray-500">{t('allowed_formats')} up to 10MB</p>
                            </div>
                        </div>
                        {existingPhotos.length > 0 && (
                            <div className="mt-4">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">{t('photos')}:</h4>
                                <div className="grid grid-cols-3 gap-4">
                                    {existingPhotos.map(photo => (
                                        <div key={photo.id} className="relative group">
                                            <img
                                                src={`${BASE_URL}${photo.photo_url}`}
                                                alt="Reservation"
                                                className="w-full h-32 object-cover rounded-lg"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleDeletePhoto(photo.id)}
                                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t border-gray-200">
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => navigate('/reservations')}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white transition-colors ${submitting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                        >
                            {submitting ? (
                                <>
                                    <svg className="animate-spin h-4 w-4 text-white"
                                        xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                            strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>{t('saving')}</span>
                                </>
                            ) : t('modify')}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default EditReservationForm;